import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DashboardHeader from '../layouts/dashboard_header';
import SideMenu from '../layouts/dashboard_menu';
import DashboardFooter from '../layouts/dashboard_footer';
import { useAppName, useBaseUrl } from '../../utils/utils';
import './ModernDashboard.css';

interface DashboardAccessItem {
    key: string;
    name: string;
    route: string;
    endpoint: string;
    is_default: boolean;
}

interface BillingAccount {
    id: string;
    account_type: string;
    account_number: string;
    account_name: string;
    balance: string;
    credit_limit: string;
    currency_code: string;
    currency_symbol: string;
    is_active: boolean;
    auto_recharge: boolean;
    recharge_threshold: string;
    recharge_amount: string;
    billing_address: string | null;
    tax_id: string | null;
    last_transaction_at: string | null;
    created_at: string | null;
}

interface BillingTransaction {
    id: string;
    amount: string;
    balance_after: string;
    transaction_type: string;
    status: string;
    description: string;
    reference: string | null;
    created_at: string | null;
}

interface SubscriptionPayload {
    dashboard_access: DashboardAccessItem[];
    access_mode: string;
    staff: {
        id: number;
        fname: string;
        lname: string;
        email: string;
        title: string;
    };
    clinic: {
        id: number;
        name: string;
    };
    institution: {
        id: number;
        name: string;
    };
    subscription: {
        status: string;
        billing_model: string;
        primary_scope: string;
        clinic_account: BillingAccount | null;
        institution_account: BillingAccount | null;
        recent_transactions: BillingTransaction[];
        summary: {
            has_clinic_account: boolean;
            has_institution_account: boolean;
            auto_recharge_enabled: boolean;
            recent_transaction_count: number;
            current_balance: string;
            credit_limit: string;
        };
    };
}

const formatMoney = (amount: string | number, currencySymbol = 'KSh') => {
    const numeric = Number(amount || 0);
    return `${currencySymbol} ${numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDateTime = (value: string | null) => {
    if (!value) return '—';
    const date = new Date(value);
    return date.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const transactionBadgeClass = (status: string) => {
    if (status === 'completed') return 'uz-badge-active';
    if (status === 'pending') return 'uz-badge-pending';
    return 'uz-badge-closed';
};

const MySubscription: React.FC = () => {
    const appName = useAppName();
    const baseUrl = useBaseUrl();
    const [data, setData] = useState<SubscriptionPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        document.title = `${appName} - My Subscription`;
        const rootEl = document.getElementById('root');
        if (rootEl) rootEl.setAttribute('data-layout', 'semi-box');
    }, [appName]);

    const fetchSubscription = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(`${baseUrl}core/api/dashboard/subscription/`, {
                withCredentials: true,
                timeout: 15000,
            });
            if (response.data?.status === 'success') {
                setData(response.data.data);
            } else {
                setError(response.data?.message || 'Failed to load subscription data');
            }
        } catch (err: any) {
            console.error('Subscription fetch error:', err);
            setError(err?.response?.data?.message || 'Failed to load subscription data');
        } finally {
            setLoading(false);
        }
    }, [baseUrl]);

    useEffect(() => {
        fetchSubscription();
    }, [fetchSubscription]);

    const primaryAccount = data?.subscription.clinic_account || data?.subscription.institution_account || null;

    return (
        <div id="layout-wrapper">
            <DashboardHeader />
            <SideMenu />
            <div className="vertical-overlay" />
            <div className="main-content">
                <div className="page-content uz-dash-page" style={{ background: '#f8fcfb' }}>
                    <div className="container-fluid">
                        {loading ? (
                            <div className="uz-skeleton" style={{ height: 420 }} />
                        ) : (
                            <>
                                {error && (
                                    <div className="alert alert-warning d-flex align-items-center mb-3" role="alert">
                                        <i className="bx bx-error-circle fs-5 me-2" />
                                        <div>{error}</div>
                                        <button className="btn btn-sm btn-outline-warning ms-auto" onClick={fetchSubscription}>Retry</button>
                                    </div>
                                )}

                                <div className="uz-dash-banner mb-4">
                                    <div className="row align-items-center g-3">
                                        <div className="col-lg-8">
                                            <div className="uz-date mb-1">Subscription & Billing Access</div>
                                            <h2>My Subscription</h2>
                                            <p>
                                                Shared billing and subscription page for the <strong>General Service Provider</strong>, <strong>Clinician</strong>, and <strong>Facility</strong> dashboards.
                                            </p>
                                        </div>
                                        <div className="col-lg-4 d-flex justify-content-lg-end gap-2 flex-wrap">
                                            <Link to="/institution/home" className="btn btn-light btn-sm rounded-pill px-3">
                                                <i className="bx bx-home me-1" /> Dashboard Home
                                            </Link>
                                            <button className="btn btn-outline-light btn-sm rounded-pill px-3" onClick={fetchSubscription}>
                                                <i className="bx bx-refresh me-1" /> Refresh
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-4 mb-4">
                                    <div className="col-lg-8">
                                        <div className="uz-panel h-100">
                                            <div className="uz-panel-header">
                                                <h6 className="uz-section-title mb-0"><i className="bx bx-credit-card text-primary" /> Subscription Summary</h6>
                                            </div>
                                            <div className="uz-panel-body">
                                                <div className="row g-3 mb-3">
                                                    <div className="col-md-4">
                                                        <div className="uz-stat-card h-100">
                                                            <div className="uz-stat-label">Status</div>
                                                            <div className="uz-stat-value text-capitalize">{data?.subscription.status || 'inactive'}</div>
                                                            <div className="uz-stat-sub">{data?.subscription.billing_model || 'account based access'}</div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <div className="uz-stat-card h-100">
                                                            <div className="uz-stat-label">Primary Scope</div>
                                                            <div className="uz-stat-value text-capitalize">{data?.subscription.primary_scope || 'unprovisioned'}</div>
                                                            <div className="uz-stat-sub">Current billing ownership</div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <div className="uz-stat-card h-100">
                                                            <div className="uz-stat-label">Current Balance</div>
                                                            <div className="uz-stat-value">{formatMoney(data?.subscription.summary.current_balance || 0, primaryAccount?.currency_symbol || 'KSh')}</div>
                                                            <div className="uz-stat-sub">Credit limit {formatMoney(data?.subscription.summary.credit_limit || 0, primaryAccount?.currency_symbol || 'KSh')}</div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <div className="border rounded-3 p-3 h-100">
                                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                                <h6 className="mb-0">Clinic Account</h6>
                                                                <span className={`uz-badge ${data?.subscription.clinic_account?.is_active ? 'uz-badge-active' : 'uz-badge-closed'}`}>
                                                                    {data?.subscription.clinic_account ? (data.subscription.clinic_account.is_active ? 'Active' : 'Inactive') : 'Not Set'}
                                                                </span>
                                                            </div>
                                                            {data?.subscription.clinic_account ? (
                                                                <>
                                                                    <div className="small text-muted mb-1">{data.subscription.clinic_account.account_name}</div>
                                                                    <div className="small text-muted mb-1">{data.subscription.clinic_account.account_number}</div>
                                                                    <div className="fw-semibold mb-1">{formatMoney(data.subscription.clinic_account.balance, data.subscription.clinic_account.currency_symbol)}</div>
                                                                    <div className="small text-muted">Auto recharge: {data.subscription.clinic_account.auto_recharge ? 'Enabled' : 'Disabled'}</div>
                                                                </>
                                                            ) : (
                                                                <div className="small text-muted">No clinic billing account provisioned yet.</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="border rounded-3 p-3 h-100">
                                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                                <h6 className="mb-0">Institution Account</h6>
                                                                <span className={`uz-badge ${data?.subscription.institution_account?.is_active ? 'uz-badge-active' : 'uz-badge-closed'}`}>
                                                                    {data?.subscription.institution_account ? (data.subscription.institution_account.is_active ? 'Active' : 'Inactive') : 'Not Set'}
                                                                </span>
                                                            </div>
                                                            {data?.subscription.institution_account ? (
                                                                <>
                                                                    <div className="small text-muted mb-1">{data.subscription.institution_account.account_name}</div>
                                                                    <div className="small text-muted mb-1">{data.subscription.institution_account.account_number}</div>
                                                                    <div className="fw-semibold mb-1">{formatMoney(data.subscription.institution_account.balance, data.subscription.institution_account.currency_symbol)}</div>
                                                                    <div className="small text-muted">Auto recharge: {data.subscription.institution_account.auto_recharge ? 'Enabled' : 'Disabled'}</div>
                                                                </>
                                                            ) : (
                                                                <div className="small text-muted">No institution billing account provisioned yet.</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-lg-4">
                                        <div className="uz-panel h-100">
                                            <div className="uz-panel-header">
                                                <h6 className="uz-section-title mb-0"><i className="bx bx-layout text-success" /> Dashboard Access</h6>
                                            </div>
                                            <div className="uz-panel-body">
                                                <div className="small text-muted mb-3">{data?.access_mode}</div>
                                                {(data?.dashboard_access || []).map(item => (
                                                    <Link key={item.key} to={item.route} className="uz-quick-card text-start mb-2">
                                                        <h6 className="mb-1">{item.name}</h6>
                                                        <small>{item.is_default ? 'Default fallback dashboard' : 'Available dashboard'}</small>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-4 mb-4">
                                    <div className="col-lg-12">
                                        <div className="uz-panel h-100">
                                            <div className="uz-panel-header">
                                                <h6 className="uz-section-title mb-0"><i className="bx bx-history text-warning" /> Recent Billing Activity</h6>
                                            </div>
                                            <div className="uz-panel-body">
                                                {(!data?.subscription.recent_transactions || data.subscription.recent_transactions.length === 0) ? (
                                                    <div className="text-center py-4 text-muted">
                                                        <i className="bx bx-receipt fs-1 d-block mb-2 opacity-50" />
                                                        No recent billing transactions found.
                                                    </div>
                                                ) : (
                                                    data.subscription.recent_transactions.map(tx => (
                                                        <div key={tx.id} className="uz-collab-row">
                                                            <div className="uz-quick-icon flex-shrink-0" style={{ width: 38, height: 38, fontSize: '1rem', background: 'rgba(64,81,137,.08)', color: '#405189' }}>
                                                                <i className="bx bx-receipt" />
                                                            </div>
                                                            <div className="flex-grow-1 min-width-0">
                                                                <div className="fw-semibold text-truncate" style={{ fontSize: '.84rem' }}>{tx.description}</div>
                                                                <div style={{ fontSize: '.72rem', color: '#878a99' }}>
                                                                    {tx.transaction_type} • {formatDateTime(tx.created_at)}
                                                                    {tx.reference ? ` • Ref ${tx.reference}` : ''}
                                                                </div>
                                                            </div>
                                                            <div className="text-end me-3">
                                                                <div className="fw-semibold">{formatMoney(tx.amount, primaryAccount?.currency_symbol || 'KSh')}</div>
                                                                <div style={{ fontSize: '.72rem', color: '#878a99' }}>Bal: {formatMoney(tx.balance_after, primaryAccount?.currency_symbol || 'KSh')}</div>
                                                            </div>
                                                            <span className={`uz-badge ${transactionBadgeClass(tx.status)}`}>{tx.status}</span>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <DashboardFooter />
            </div>
        </div>
    );
};

export default MySubscription;
